import React, { useState, useEffect } from "react"
import { ArrowRight, CheckCircle2, Clock, RotateCcw, XCircle } from "lucide-react"
import { Card, CardContent } from "../../../components/ui/card"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../components/ui/table"
import { listOrgOrders, formatOrderDisplayId } from "../../../utils/orderHelpers"

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0)

const formatDate = (value) => {
  const ms = value?.toMillis ? value.toMillis() : new Date(value).getTime()
  if (!ms) return "—"
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const statusConfig = {
  paid: { label: "Success", icon: CheckCircle2, className: "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-600" },
  pending: { label: "Pending", icon: Clock, className: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-600" },
  refunded: { label: "Refunded", icon: RotateCcw, className: "bg-muted text-muted-foreground" },
  failed: { label: "Failed", icon: XCircle, className: "bg-red-100 text-destructive dark:bg-red-950" },
}

const StatusPill = ({ status }) => {
  const config = statusConfig[status] || statusConfig.pending
  const Icon = config.icon
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${config.className}`}>
      <Icon className="size-3" />
      {config.label}
    </span>
  )
}

const RecentOrdersSection = ({ orgId, onNavigateTab }) => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    listOrgOrders(orgId).then(({ orders: data }) => {
      setOrders((data || [])?.slice(0, 6))
      setLoading(false)
    })
  }, [orgId])

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-foreground">Recent Orders</h2>
        <button
          onClick={() => onNavigateTab?.("commerce-orders")}
          className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          See All Orders
          <ArrowRight className="size-3.5" />
        </button>
      </div>

      <Card className="shadow-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 4 })?.map((_, i) => <div key={i} className="h-10 rounded-lg bg-muted animate-pulse" />)}
            </div>
          ) : !orders?.length ? (
            <div className="p-8 text-center text-muted-foreground">No orders yet. Orders will show up here as customers purchase your products.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.orderId} className="cursor-pointer" onClick={() => onNavigateTab?.("commerce-orders")}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{formatOrderDisplayId(order.orderId)}</TableCell>
                    <TableCell className="font-medium text-foreground">{order.customerName || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{order.productTitle}</TableCell>
                    <TableCell className="font-medium text-foreground">{formatCurrency(order.amount)}</TableCell>
                    <TableCell>
                      <StatusPill status={order.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default RecentOrdersSection